/**
 * mandate-control.ts (P4-S4) — the Kontrollen & Entscheid vocabulary, wire DTOs and the four PURE
 * evaluators (classifySignatureScore, evaluateSigningRule, evaluateCompleteness, decideOverall) plus
 * normalizeSignerName.
 *
 * All four evaluators are pure (rule #2: business logic in code, not SQL): no IO, no clock, no PII
 * side effects. The per-check result shapes carry ids/codes/scores only (rule #6) — never names;
 * display names are resolved on the fly by the read surface. The one storage reference is a signer's
 * returned_crop_ref (the object-store key of the crop, for the side-by-side view).
 *
 * [Source: docs/product/p4-p6-slices/p4-S4-kontrollen-entscheid-design.md] (§2.1, §3.1, §3.4, §4)
 * [Source: packages/shared/src/firms.ts] (FirmSignatureType — the register SIGNATURE vocab)
 * [Source: packages/shared/src/policy.ts] (LineOfBusiness)
 * [Source: packages/shared/src/insurance-relationship.ts] (InsuranceRelationshipDto)
 */

import type { FirmSignatureType } from './firms';
import type { LineOfBusiness } from './policy';
import type { InsuranceRelationshipDto } from './insurance-relationship';
import type { RuecklaufCategory } from './ruecklauf';
import type { MandateScopeExcludeReason, MandateScopeItemDto } from './mandate-scope';

// ── Vocabularies ─────────────────────────────────────────────────────────────

/** Per-signer signature-control result (Stefan 7.3 Schritt 15/16). */
export const SIGNATURE_RESULT = {
  BESTANDEN: 'bestanden',
  UNSICHER: 'unsicher',
  BEFUND: 'befund',
  FEHLT: 'fehlt',
  NEUE_REFERENZ: 'neue_referenz',
  NEUE_REFERENZ_OFFEN: 'neue_referenz_offen',
  NICHT_ZUORDENBAR: 'nicht_zuordenbar',
  KEIN_KONTAKT: 'kein_kontakt',
  DIGITAL_BESTANDEN: 'digital_bestanden',
  REFERENZ_NICHT_MOEGLICH: 'referenz_nicht_moeglich',
  BESTAETIGT: 'bestaetigt',
  ABGELEHNT: 'abgelehnt',
} as const;
export type SignatureResult = (typeof SIGNATURE_RESULT)[keyof typeof SIGNATURE_RESULT];

/** Zeichnungsberechtigung result (Stefan 7.3 Schritt 17; only juristische Personen). */
export const SIGNING_AUTHORITY_RESULT = {
  ERFUELLT: 'erfuellt',
  NICHT_ERFUELLT: 'nicht_erfuellt',
  NICHT_VERIFIZIERT: 'nicht_verifiziert',
  NICHT_ANWENDBAR: 'nicht_anwendbar',
  BESTAETIGT: 'bestaetigt',
} as const;
export type SigningAuthorityResultCode =
  (typeof SIGNING_AUTHORITY_RESULT)[keyof typeof SIGNING_AUTHORITY_RESULT];

/**
 * Vollständigkeitsampel (Stefan 7.3 Schritt 18 / 7.5). `nicht_anwendbar`: a Brokermandat, whose
 * return is checked against its Mandatsumfang instead (P5 Schritt 19).
 */
export const COMPLETENESS_COLOUR = { GRUEN: 'gruen', GELB: 'gelb', ROT: 'rot', NICHT_ANWENDBAR: 'nicht_anwendbar' } as const;
export type CompletenessColour = (typeof COMPLETENESS_COLOUR)[keyof typeof COMPLETENESS_COLOUR];

/** The consolidated verdict (Stefan 7.3 Schritt 19). */
export const CONTROL_OVERALL = { AUTO_DEFINITIV: 'auto_definitiv', REVIEW: 'review' } as const;
export type ControlOverall = (typeof CONTROL_OVERALL)[keyof typeof CONTROL_OVERALL];

/** The broker's Entscheid on a review run (Stefan 7.3 Schritt 19). */
export const REVIEW_DECISION = {
  GENEHMIGEN: 'genehmigen',
  NACHFORDERUNG: 'nachforderung',
  ABBRECHEN: 'abbrechen',
} as const;
export type ReviewDecision = (typeof REVIEW_DECISION)[keyof typeof REVIEW_DECISION];

/** The persisted decision verb on the run row (past tense; distinct from the request verb). */
export const CONTROL_DECISION = {
  GENEHMIGT: 'genehmigt',
  NACHFORDERUNG: 'nachforderung',
  ABGEBROCHEN: 'abgebrochen',
} as const;
export type ControlDecision = (typeof CONTROL_DECISION)[keyof typeof CONTROL_DECISION];

/** The run trigger. */
export const CONTROL_TRIGGER = {
  RUECKLAUF: 'ruecklauf',
  NACHREICHUNG: 'nachreichung',
  DIGITAL: 'digital',
} as const;
export type ControlTrigger = (typeof CONTROL_TRIGGER)[keyof typeof CONTROL_TRIGGER];

/** The register SIGNATURE vocab (shared alias of the firms signatory union). */
export type SignatureKind = FirmSignatureType;

/** Fixed rules of Stefan 7.10 (constants, NOT settings — see OPEN-1). */
export const NEW_REFERENCE_ALWAYS_CONFIRM = true as const;
export const OPTICAL_CHECK_ON_DIGITAL_SIGNATURE = false as const;

/** KMU Soll-Deckungskatalog over the line_of_business enum (bvg not representable, OPEN-6). */
export const SOLL_KATALOG_KMU: readonly LineOfBusiness[] = ['haftpflicht', 'sach', 'uvg', 'ktg'];

/** The thresholds in force at check time (Stefan 7.10: stored on every run). */
export interface SignatureThresholds {
  auto: number;
  uncertain: number;
  usedDefault: boolean;
}

// ── Per-check result shapes (jsonb, ids/codes/scores only — rule #6) ──────────

/** signature_result jsonb = { signers: SignerSignatureResult[] }. */
export interface SignerSignatureResult {
  mandate_signer_id: string | null;
  person_id: string | null;
  sign_order: number;
  /** field_ref = 'sig-<sort_order+1>'. */
  field_ref: string;
  present: boolean;
  score: number | null;
  result: SignatureResult;
  /** the reference compared against, or the provisorisch one just captured. */
  reference_signature_id: string | null;
  /** object-store key of the crop from the returned doc (for the side-by-side view). */
  returned_crop_ref: string | null;
  source: 'fixture' | 'image_similarity' | 'esign_provider' | 'manual_esign';
  /** set by decide('genehmigen'). */
  confirmed_by_decision: boolean;
}

/** signing_authority_result jsonb. */
export interface SigningAuthorityResult {
  result: SigningAuthorityResultCode;
  rule: 'einzel' | 'kollektiv_2' | 'kollektiv_3' | 'unbekannt' | null;
  register_source: 'excerpt_pdf' | 'zefix_replay' | null;
  register_fetched_at: string | null;
  matches: Array<{
    /** null for a register-only signer (no Kontakt). */
    person_id: string | null;
    matched: boolean;
    signature: SignatureKind | null;
    role: string | null;
    present: boolean;
  }>;
  reason:
    | 'keine_registerdaten'
    | 'unterzeichner_nicht_im_register'
    | 'zweite_unterschrift_fehlt'
    | 'nur_prokura'
    | 'kollektiv_3_manuell'
    | null;
  confirmed_by_decision: boolean;
  vollmacht_confirmed: boolean;
}

/** completeness_result jsonb. */
export interface CompletenessFinding {
  code:
    | 'keine_angaben'
    | 'angabe_ohne_dokument'
    | 'mindestfelder_fehlen'
    | 'sollkatalog_fehlt'
    | 'versicherungsnehmer_abweichend'
    | 'seiten_unvollstaendig';
  relationship_id?: string;
  line_of_business?: LineOfBusiness;
  missing?: string[];
  severity: 'gelb' | 'rot' | 'hinweis';
}
export interface CompletenessResult {
  colour: CompletenessColour;
  findings: CompletenessFinding[];
  /** explicit, never guessed (OPEN-6). */
  not_checked: Array<'versicherungsnehmer' | 'seitenvollstaendigkeit' | 'mindestfelder_laufzeit_praemie'>;
  relationship_count: number;
  document_count: number;
}

// ── Pure evaluators ───────────────────────────────────────────────────────────

/**
 * Classify a machine signature score against the tenant thresholds (Stefan 7.5).
 *   score >= auto               -> bestanden
 *   uncertain <= score < auto   -> unsicher
 *   score < uncertain | null    -> befund
 */
export function classifySignatureScore(
  score: number | null,
  t: Pick<SignatureThresholds, 'auto' | 'uncertain'>,
): 'bestanden' | 'unsicher' | 'befund' {
  if (score === null || !Number.isFinite(score)) return 'befund';
  if (score >= t.auto) return 'bestanden';
  if (score >= t.uncertain) return 'unsicher';
  return 'befund';
}

/**
 * Normalize a signer name for in-memory register matching: lower-case, German umlaut folding
 * (ä->ae, ö->oe, ü->ue, ß->ss), diacritics stripped, hyphen/space collapsed. Exact last+first key.
 * Pure; no PII leaves this function (the key is only compared, never stored).
 */
export function normalizeSignerName(last: string, first: string): string {
  const fold = (s: string): string =>
    (s ?? '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // strip remaining combining marks
      .replace(/[-\s]+/g, ' ')
      .trim();
  return `${fold(last)}\u0000${fold(first)}`;
}

/**
 * Evaluate the Zeichnungsregel (Stefan 7.3 Schritt 17, OPEN-9) over the signatures actually present,
 * matched by name to the firm's effective register signatory set. Pure; returns ids/codes only.
 */
export function evaluateSigningRule(input: {
  signers: Array<{ person_id: string | null; last: string; first: string; present: boolean }>;
  register: {
    source: 'excerpt_pdf' | 'zefix_replay';
    fetched_at: string | null;
    signatories: Array<{ last: string; first: string; role: string; signature: SignatureKind }>;
  } | null;
}): SigningAuthorityResult {
  const base = {
    confirmed_by_decision: false,
    vollmacht_confirmed: false,
  };

  // No register data at all -> cannot verify.
  if (!input.register) {
    return {
      ...base,
      result: SIGNING_AUTHORITY_RESULT.NICHT_VERIFIZIERT,
      rule: null,
      register_source: null,
      register_fetched_at: null,
      matches: input.signers.map((s) => ({
        person_id: s.person_id,
        matched: false,
        signature: null,
        role: null,
        present: s.present,
      })),
      reason: 'keine_registerdaten',
    };
  }

  const byName = new Map<string, { role: string; signature: SignatureKind }>();
  for (const sig of input.register.signatories) {
    byName.set(normalizeSignerName(sig.last, sig.first), { role: sig.role, signature: sig.signature });
  }

  const matches = input.signers.map((s) => {
    const hit = byName.get(normalizeSignerName(s.last, s.first));
    return {
      person_id: s.person_id,
      matched: hit != null,
      signature: hit ? hit.signature : null,
      role: hit ? hit.role : null,
      present: s.present,
    };
  });

  const common = {
    ...base,
    register_source: input.register.source,
    register_fetched_at: input.register.fetched_at,
    matches,
  };

  // A present signer who is not in the register -> nicht erfüllt (UC7).
  if (matches.some((m) => m.present && !m.matched)) {
    return { ...common, result: SIGNING_AUTHORITY_RESULT.NICHT_ERFUELLT, rule: 'unbekannt', reason: 'unterzeichner_nicht_im_register' };
  }

  // Each register entry counts once: the same person listed twice is still one signature.
  const signedEntries = new Map<string, SignatureKind>();
  input.signers.forEach((s, i) => {
    const m = matches[i]!;
    if (m.present && m.matched) signedEntries.set(normalizeSignerName(s.last, s.first), m.signature!);
  });
  const sigs = [...signedEntries.values()];

  // Einzelunterschrift (incl. Prokura Einzel) -> erfüllt (Stefan OPEN-9).
  if (sigs.includes('einzel') || sigs.includes('prokura_einzel')) {
    return { ...common, result: SIGNING_AUTHORITY_RESULT.ERFUELLT, rule: 'einzel', reason: null };
  }

  // Kollektiv zu dreien -> immer menschliche Vorlage (rare; reviewer decides).
  if (sigs.includes('kollektiv_3') || sigs.includes('prokura_kollektiv_3')) {
    return { ...common, result: SIGNING_AUTHORITY_RESULT.NICHT_VERIFIZIERT, rule: 'kollektiv_3', reason: 'kollektiv_3_manuell' };
  }

  // Kollektiv zu zweien: at least two collective signatures, at least one non-Prokura (Stefan OPEN-9).
  const collective = sigs.filter((s) => s === 'kollektiv_2' || s === 'prokura_kollektiv_2');
  if (collective.length >= 2) {
    if (collective.some((s) => s === 'kollektiv_2')) {
      return { ...common, result: SIGNING_AUTHORITY_RESULT.ERFUELLT, rule: 'kollektiv_2', reason: null };
    }
    return { ...common, result: SIGNING_AUTHORITY_RESULT.NICHT_ERFUELLT, rule: 'kollektiv_2', reason: 'nur_prokura' };
  }

  // Only one collective signature present (or none recognised) -> second signature missing.
  return { ...common, result: SIGNING_AUTHORITY_RESULT.NICHT_ERFUELLT, rule: 'kollektiv_2', reason: 'zweite_unterschrift_fehlt' };
}

/**
 * Evaluate Vollständigkeit (Stefan 7.3 Schritt 18 / 7.5) over the declared insurance_relationship rows
 * and the returned documents. Only the subset the data supports; the rest is explicitly `not_checked`
 * (OPEN-6). Never blocks the status (grün/gelb pass; rot forces review — the service, not here).
 * Pure.
 */
export function evaluateCompleteness(input: {
  segment: 'kmu' | 'priv';
  relationships: InsuranceRelationshipDto[];
  returnedDocuments: Array<{ document_id: string; category: RuecklaufCategory }>;
}): CompletenessResult {
  const findings: CompletenessFinding[] = [];
  const not_checked: CompletenessResult['not_checked'] = [
    'versicherungsnehmer',
    'seitenvollstaendigkeit',
    'mindestfelder_laufzeit_praemie',
  ];
  // The Analysemandat itself is not a backing document for a declared insurance.
  const backingDocs = input.returnedDocuments.filter((d) => d.category !== 'analysemandat' && d.category !== 'brokermandat');

  let colour: CompletenessColour = COMPLETENESS_COLOUR.GRUEN;
  const worse = (next: CompletenessColour): void => {
    if (next === COMPLETENESS_COLOUR.ROT) colour = COMPLETENESS_COLOUR.ROT;
    else if (next === COMPLETENESS_COLOUR.GELB && colour !== COMPLETENESS_COLOUR.ROT) colour = COMPLETENESS_COLOUR.GELB;
  };

  if (input.relationships.length === 0) {
    findings.push({ code: 'keine_angaben', severity: 'rot' });
    worse(COMPLETENESS_COLOUR.ROT);
  }

  for (const r of input.relationships) {
    if ((r.source === 'manual' || r.source === 'portal') && r.source_document_id == null && r.policy_id == null) {
      findings.push({ code: 'angabe_ohne_dokument', relationship_id: r.id, severity: 'gelb' });
      worse(COMPLETENESS_COLOUR.GELB);
      continue;
    }
    if (r.source === 'extracted') {
      const missing: string[] = [];
      if (r.policy_number == null || r.policy_number === '') missing.push('policy_number');
      if (r.line_of_business == null) missing.push('line_of_business');
      if (missing.length > 0) {
        findings.push({ code: 'mindestfelder_fehlen', relationship_id: r.id, missing, severity: 'gelb' });
        worse(COMPLETENESS_COLOUR.GELB);
      }
    }
  }

  // KMU Soll-Deckungskatalog — a Hinweis only (never a guessed colour, OPEN-6).
  if (input.segment === 'kmu') {
    const present = new Set(input.relationships.map((r) => r.line_of_business).filter((x): x is LineOfBusiness => x != null));
    for (const lob of SOLL_KATALOG_KMU) {
      if (!present.has(lob)) {
        findings.push({ code: 'sollkatalog_fehlt', line_of_business: lob, severity: 'hinweis' });
      }
    }
  }

  return {
    colour,
    findings,
    not_checked,
    relationship_count: input.relationships.length,
    document_count: backingDocs.length,
  };
}

/** The completeness result of a Brokermandat: not evaluated, the scope check replaces it. */
export function completenessNotApplicable(): CompletenessResult {
  return { colour: COMPLETENESS_COLOUR.NICHT_ANWENDBAR, findings: [], not_checked: [], relationship_count: 0, document_count: 0 };
}

// ── Mandatsumfang bestätigen (Brokermandat, P5 Schritt 19) ─────────────────────

/** The scope check of a broker return (mandate_control_run.scope_result, 0187). */
export const SCOPE_RESULT = {
  BESTAETIGT: 'bestaetigt',
  GEAENDERT: 'geaendert',
  NICHT_PRUEFBAR: 'nicht_pruefbar',
  NICHT_GEPRUEFT: 'nicht_geprueft',
} as const;
export type ScopeResult = (typeof SCOPE_RESULT)[keyof typeof SCOPE_RESULT];

export const SCOPE_RESULT_LABEL: Record<ScopeResult, string> = {
  bestaetigt: 'Mandatsumfang bestätigt',
  geaendert: 'Mandatsumfang geändert',
  nicht_pruefbar: 'Mandatsumfang durch den Broker zu bestätigen',
  nicht_geprueft: 'Kein Mandatsumfang erfasst',
};

/** The scope results the broker must confirm before «Genehmigen» (400 scope_confirmation_required). */
export const SCOPE_RESULTS_REQUIRING_CONFIRMATION: readonly ScopeResult[] = [SCOPE_RESULT.GEAENDERT, SCOPE_RESULT.NICHT_PRUEFBAR];

/** The review reason codes of the Brokermandat controls (ids and codes only, rule #6). */
export const CONTROL_REVIEW_REASON = {
  UMFANG_BESTAETIGUNG_ERFORDERLICH: 'umfang_bestaetigung_erforderlich',
  UMFANG_GEAENDERT: 'umfang_geaendert',
  REGISTER_CHANGED_SINCE_CHECK: 'register_changed_since_check',
} as const;
export type ControlReviewReason = (typeof CONTROL_REVIEW_REASON)[keyof typeof CONTROL_REVIEW_REASON];

/** A policy number for comparison: upper case, spaces, dots, dashes and any other separator removed. Pure. */
export function normalizePolicyNumber(n: string | null | undefined): string {
  return (n ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Compare the stored Mandatsumfang with what the return brought (Stefan P5 Schritt 19). An F3 row
 * the return created is covered when it is an item's declared insurance, rides on an item's policy,
 * or names the same carrier and policy number as an item (included or not); any other row is an
 * addition. A strike on paper has no machine signal, so a return without additions is confirmed
 * only when the tenant opted in (`scopeAutoConfirm`); otherwise the broker confirms it.
 *   no items             -> nicht_geprueft, no reason (no scope stored)
 *   additions            -> geaendert, umfang_geaendert (whatever the flag)
 *   none, flag off       -> nicht_pruefbar, umfang_bestaetigung_erforderlich
 *   none, flag on        -> bestaetigt, no reason
 * Pure; ids only in the result.
 */
export function evaluateScope(input: {
  items: Array<{
    id: string;
    policyId: string | null;
    relationshipId: string | null;
    included: boolean;
    carrierKey: string;
    policyNumberNorm: string;
  }>;
  returnedRelationships: Array<{ id: string; carrierKey: string; policyNumberNorm: string; policyId: string | null }>;
  scopeAutoConfirm: boolean;
}): { result: ScopeResult; additions: string[]; reasons: ControlReviewReason[] } {
  if (input.items.length === 0) return { result: SCOPE_RESULT.NICHT_GEPRUEFT, additions: [], reasons: [] };

  const covered = (r: (typeof input.returnedRelationships)[number]): boolean => {
    const number = normalizePolicyNumber(r.policyNumberNorm);
    return input.items.some(
      (i) =>
        i.relationshipId === r.id ||
        (r.policyId !== null && i.policyId === r.policyId) ||
        (number !== '' && i.carrierKey === r.carrierKey && normalizePolicyNumber(i.policyNumberNorm) === number),
    );
  };
  const additions = input.returnedRelationships.filter((r) => !covered(r)).map((r) => r.id);

  if (additions.length > 0) {
    return { result: SCOPE_RESULT.GEAENDERT, additions, reasons: [CONTROL_REVIEW_REASON.UMFANG_GEAENDERT] };
  }
  if (!input.scopeAutoConfirm) {
    return { result: SCOPE_RESULT.NICHT_PRUEFBAR, additions: [], reasons: [CONTROL_REVIEW_REASON.UMFANG_BESTAETIGUNG_ERFORDERLICH] };
  }
  return { result: SCOPE_RESULT.BESTAETIGT, additions: [], reasons: [] };
}

/** The finding codes a Nachforderung asks the customer about, in the customer's words. */
export const NACHFORDERUNG_BEFUND_LABEL: Record<CompletenessFinding['code'] | 'unterschrift_erneut', string> = {
  keine_angaben: 'Angaben zu Ihren bestehenden Versicherungen',
  angabe_ohne_dokument: 'Policen oder Belege zu den angegebenen Versicherungen',
  mindestfelder_fehlen: 'Policennummer und Sparte zu den angegebenen Versicherungen',
  sollkatalog_fehlt: 'Angaben zu den fehlenden Grundversicherungen',
  versicherungsnehmer_abweichend: 'Angaben zum Versicherungsnehmer',
  seiten_unvollstaendig: 'die fehlenden Seiten des Dokuments',
  unterschrift_erneut: 'eine erneute Unterschrift auf dem Mandat',
};

/**
 * The Nachforderung's list of open points from a run's results: the completeness findings (not the
 * Hinweise) and a missing or rejected signature, one line each in words. Pure; codes in, no PII.
 */
export function nachforderungBefundliste(signers: SignerSignatureResult[], completeness: CompletenessResult): string {
  const codes: Array<keyof typeof NACHFORDERUNG_BEFUND_LABEL> = [];
  for (const f of completeness.findings) {
    if (f.severity !== 'hinweis' && !codes.includes(f.code)) codes.push(f.code);
  }
  if (signers.some((s) => s.result === SIGNATURE_RESULT.BEFUND || s.result === SIGNATURE_RESULT.FEHLT)) {
    codes.push('unterschrift_erneut');
  }
  return codes.length > 0 ? codes.map((c) => `- ${NACHFORDERUNG_BEFUND_LABEL[c]}`).join('\n') : '- keine';
}

/** The review reasons in words (the Prüfbericht shows why a run went to review). */
export function controlReviewReasonLabel(code: string): string {
  const fixed: Record<string, string> = {
    umfang_bestaetigung_erforderlich: 'Mandatsumfang durch den Broker zu bestätigen',
    umfang_geaendert: 'Der Rücklauf nennt Versicherungen ausserhalb des Mandatsumfangs',
    register_changed_since_check: 'Der Handelsregisterstand hat sich seit der Registerprüfung geändert',
    completeness_rot: 'Keine Angaben zu den bestehenden Versicherungen',
    signing_authority_nicht_erfuellt: 'Zeichnungsregel nicht erfüllt',
    signing_authority_nicht_verifiziert: 'Zeichnungsregel nicht prüfbar',
  };
  if (fixed[code]) return fixed[code]!;
  if (code.startsWith('signature_')) {
    const result = code.slice('signature_'.length) as SignatureResult;
    return `Unterschrift: ${SIGNATURE_RESULT_LABEL[result] ?? result}`;
  }
  return code;
}

/** Every signature result in words (one map, api and web). */
export const SIGNATURE_RESULT_LABEL: Record<SignatureResult, string> = {
  bestanden: 'bestanden',
  unsicher: 'unsicher',
  befund: 'Befund',
  fehlt: 'fehlt',
  neue_referenz: 'neue Referenz erfasst',
  neue_referenz_offen: 'Referenz noch nicht bestätigt',
  nicht_zuordenbar: 'nicht zuordenbar',
  kein_kontakt: 'kein Kontakt hinterlegt',
  digital_bestanden: 'digital bestanden',
  referenz_nicht_moeglich: 'keine Referenz möglich',
  bestaetigt: 'bestätigt',
  abgelehnt: 'abgelehnt',
};

/** The signature results treated as "clean" (no review required). */
const SIGNATURE_OK: ReadonlySet<SignatureResult> = new Set<SignatureResult>([
  SIGNATURE_RESULT.BESTANDEN,
  SIGNATURE_RESULT.DIGITAL_BESTANDEN,
  SIGNATURE_RESULT.BESTAETIGT,
]);

/**
 * Consolidate the three per-check results into the overall verdict + the finding reason codes
 * (Stefan 7.3 Schritt 19). Pure; reasons are stable codes, never free text.
 */
export function decideOverall(
  sig: SignerSignatureResult[],
  auth: SigningAuthorityResult,
  comp: CompletenessResult,
  /** Further review reasons (the Brokermandat scope and register checks); each forces review. */
  extraReasons: readonly string[] = [],
): { overall: ControlOverall; nachforderung_required: boolean; reasons: string[] } {
  const reasons: string[] = [];

  for (const s of sig) {
    if (!SIGNATURE_OK.has(s.result)) {
      const code = `signature_${s.result}`;
      if (!reasons.includes(code)) reasons.push(code);
    }
  }
  if (
    auth.result === SIGNING_AUTHORITY_RESULT.NICHT_ERFUELLT ||
    auth.result === SIGNING_AUTHORITY_RESULT.NICHT_VERIFIZIERT
  ) {
    reasons.push(`signing_authority_${auth.result}`);
  }
  if (comp.colour === COMPLETENESS_COLOUR.ROT) {
    reasons.push('completeness_rot');
  }
  for (const r of extraReasons) {
    if (!reasons.includes(r)) reasons.push(r);
  }

  const nachforderung_required =
    sig.some((s) => s.result === SIGNATURE_RESULT.FEHLT) || comp.colour === COMPLETENESS_COLOUR.GELB;

  return {
    overall: reasons.length > 0 ? CONTROL_OVERALL.REVIEW : CONTROL_OVERALL.AUTO_DEFINITIV,
    nachforderung_required,
    reasons,
  };
}

// ── Wire DTOs ──────────────────────────────────────────────────────────────────

export interface PruefberichtDto {
  run: {
    id: string;
    vorgang_id: string;
    mandate_id: string;
    run_no: number;
    trigger: string;
    created_at: string;
    overall: ControlOverall;
    nachforderung_required: boolean;
    review_task_id: string | null;
    thresholds: SignatureThresholds;
    register_fetched_at: string | null;
    decision: ControlDecision | null;
    decided_at: string | null;
    decided_by_app_user_id: string | null;
    /** The Vollmacht document the broker relied on in the decision; null when none. */
    vollmacht_document_id: string | null;
    /** Why the run went to review (decideOverall codes); empty for an auto-definitiv run. */
    reasons?: string[];
  };
  signatures: Array<
    SignerSignatureResult & { display_name: string | null; reference_status: 'provisorisch' | 'bestaetigt' | null }
  >;
  signing_authority: Omit<SigningAuthorityResult, 'matches'> & {
    matches: Array<{
      person_id: string | null;
      display_name: string | null;
      matched: boolean;
      signature: SignatureKind | null;
      role: string | null;
      present: boolean;
    }>;
  };
  completeness: CompletenessResult;
  /** blacklist name-only viewer: display_name null everywhere. */
  names_restricted: boolean;
  /** what genehmigen needs ticked (e.g. 'signature:<person_id>', 'reference:<person_id>', 'signing_authority'). */
  required_confirmations: string[];
  runs: Array<{ id: string; run_no: number; created_at: string; overall: ControlOverall; decision: string | null }>;
  /**
   * P4-FU3: why the returned document is the signed mandate (the newest is_mandate row of the
   * Vorgang): the evidence and the code the reader found on it. null when no row is the mandate.
   */
  /**
   * The Mandatsumfang check of a broker return: the result, the stored items, the insurances the
   * return added (with their carrier and number for the broker to accept) and whether «Genehmigen»
   * needs the «Mandatsumfang bestätigt» tick. null for an Analysemandat (absent from an older api).
   */
  scope?: {
    result: ScopeResult;
    confirmationRequired: boolean;
    items: MandateScopeItemDto[];
    additions: ScopeAdditionDto[];
  } | null;
  returned_document: {
    document_id: string;
    mandate_evidence: string | null;
    code_source: string | null;
    /** The Vorgang the code on the document names; another one than run.vorgang_id = a foreign code. */
    code_vorgang_id: string | null;
    code_pages: number[];
    pages_read: number | null;
  } | null;
}

/** An insurance the return added that the scope does not cover. */
export interface ScopeAdditionDto {
  relationshipId: string;
  carrierUid: string | null;
  carrierName: string | null;
  policyNumber: string | null;
  lineOfBusiness: LineOfBusiness | null;
  sparteText: string | null;
}

/** The scope decision «Genehmigen» carries on a broker return. */
export interface PruefberichtScopeDecision {
  /** «Mandatsumfang bestätigt»: required when the scope result is geaendert or nicht_pruefbar. */
  confirmed: boolean;
  /** Additions the broker takes into the scope (ids from `scope.additions`). */
  addRelationshipIds?: string[];
  /** Scope items the customer struck (or the broker drops), each with its reason. */
  excludeItemIds?: Array<{ id: string; reason: MandateScopeExcludeReason }>;
}

export interface PruefberichtEntscheidBody {
  decision: ReviewDecision;
  /** The run the broker decides on (`run.id` of the report); 409 when a newer run exists. */
  run_id: string;
  /** person_ids whose unsicher/befund result the broker accepts. */
  confirm_signatures?: string[];
  /** person_ids whose provisorisch reference becomes bestaetigt. */
  confirm_references?: string[];
  /** accepts nicht_verifiziert / nicht_erfuellt (with vollmacht). */
  confirm_signing_authority?: boolean;
  /** A document of this mandate or its customer; stored with the decision. */
  vollmacht_document_id?: string | null;
  /** <= 500, stored on the run only. */
  note?: string | null;
  /** Brokermandat: the scope decision (genehmigen only). */
  scope?: PruefberichtScopeDecision;
}
